import { Box, Button, Typography } from '@mui/material';
import useFindJobLazy from '@src/adapters/hooks/job/useGetJob';
import Layout from '@src/components/layout/Layout';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Information from './components/Information';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import AssignCrew from './components/AssignCrew';
import JobAvailabilityDTO from '@src/adapters/dto/job/JobAvailabilityDTO';
import useSaveJobActivityLazy from '@src/adapters/hooks/job/useSaveJobActivity';
import JobAddEdit from '@src/components/job/JobAddEdit';
import { JobBookingStatusHubLocked } from '@src/constants/JobBookingStatus';
import useGetUserExportLazy from '@src/adapters/hooks/user/useGetUserExport';
import { enqueueSnackbar } from 'notistack';
import UserExportInfoDTO from '@src/adapters/dto/user/UserExportInfoDTO';
import Papa from 'papaparse';

function Job() {
  const { execute: find } = useFindJobLazy();
  const { execute: save } = useSaveJobActivityLazy();
  const { execute: getUserExport } = useGetUserExportLazy();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [job, setJob] = useState<JobDTO>();

  const id = useMemo(() => {
    return location.state.id;
  }, [location.state.id]);

  useEffect(() => {
    if (id) {
      find({ id }).then((result) => {
        setJob(result.job);
      });
    }
  }, [find, id]);

  const handleEditClick = () => {
    setOpen(true);
  };

  const handleEditClosed = (job: JobDTO | undefined) => {
    setOpen(false);
  };

  const handleExportClick = async () => {
    if (job && job.id) {
      const result = await getUserExport({
        jobId: job.id,
      });

      if (!result.userInfos || result.userInfos.length === 0) {
        enqueueSnackbar('No users have been confirmed on this job', {
          variant: 'warning',
        });
        return;
      }

      const csv = Papa.unparse(result.userInfos, {
        quotes: false,
        quoteChar: '"',
        escapeChar: '"',
        delimiter: ',',
        header: true,
        newline: '\r\n',
      });

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${new Date().toDateString()}.csv`;
      a.click();

      window.URL.revokeObjectURL(url);
    }
  };

  const handleAvailabilityClicked = async (
    availability: JobAvailabilityDTO
  ) => {
    if (id && !JobBookingStatusHubLocked.includes(availability.status)) {
      const result = await save({
        availabilityId: availability.id,
        jobId: id,
        status: availability.status,
      });

      if (job) {
        setJob((prevJob) =>
          prevJob
            ? {
                ...prevJob,
                availabilities: prevJob.availabilities.map((a) =>
                  a.id === availability.id ? { ...a, status: result.status } : a
                ),
              }
            : prevJob
        );
      }
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          padding: '0 20px 20px 20px',
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
        }}>
        <Typography variant="h4">
          {job?.role.name} ({job?.jobId})
        </Typography>
        <Button
          onClick={handleExportClick}
          color="secondary"
          variant="contained"
          size="medium">
          Export Crew Information
        </Button>
        <Button
          onClick={handleEditClick}
          color="secondary"
          variant="contained"
          size="medium">
          Edit
        </Button>
      </Box>
      {job && (
        <Box sx={(theme) => ({ paddingBottom: theme.spacing(3) })}>
          <Information job={job} />
        </Box>
      )}
      {job && (
        <AssignCrew job={job} availabilityClicked={handleAvailabilityClicked} />
      )}
      {job && <JobAddEdit job={job} close={handleEditClosed} open={open} />}
    </Layout>
  );
}

export default Job;
